import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Auth } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < 8;
  const valid = !!token && password.length >= 8 && password === confirm;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setErr(null);
    setBusy(true);
    try {
      await Auth.resetPassword(token, password);
      nav("/login", {
        replace: true,
        state: { notice: "Password reset — please sign in with your new password." },
      });
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-md panel p-8">
        <div className="flex items-center gap-3 mb-6">
          <img
            src="/favicon.svg"
            alt="LeadZeppelin"
            width={36}
            height={36}
            className="w-9 h-9 rounded-lg shadow-[0_6px_20px_rgba(94,176,255,0.28)]"
          />
          <div>
            <div className="text-lg font-semibold">LeadZeppelin</div>
            <div className="text-sm text-mut-300">by Techmellon</div>
          </div>
        </div>

        <h1 className="text-xl font-semibold mb-1">Set a new password</h1>
        <p className="text-mut-300 text-md mb-6">Choose a new password for your account.</p>

        {!token && (
          <div className="mb-4 text-sm text-accent-pink">
            This reset link is missing its token. Request a new link from the sign-in page.
          </div>
        )}

        <label className="label">New password</label>
        <input
          className="input mb-1"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {tooShort && (
          <div className="mb-2 text-xs text-accent-pink">Password must be at least 8 characters.</div>
        )}

        <label className="label mt-2">Confirm password</label>
        <input
          className="input mb-1"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
        />
        {mismatch && <div className="mb-2 text-xs text-accent-pink">Passwords do not match.</div>}

        {err && <div className="my-3 text-sm text-accent-pink">{err}</div>}

        <button
          type="submit"
          className="btn btn-primary w-full justify-center mt-4"
          disabled={busy || !valid}
        >
          {busy ? "Resetting…" : "Reset password"}
        </button>

        <div className="mt-5 text-center">
          <button
            type="button"
            className="text-sm text-accent-azure hover:underline"
            onClick={() => nav("/login")}
          >
            Back to sign in
          </button>
        </div>
      </form>
    </div>
  );
}
